import React, { useMemo } from "react";
import { ISelectProps, IMultiSelectProps, SelectInput, MultiSelectInput } from '~/components/Controls/Select';
import { FormField, IFieldProps, hasError, onChangeField } from '~/components/Form';
import { TableSortDirection } from "~/components/Table";
import { Intent } from '~/lib/intent';
import { sortByString } from "~/lib/sort";
import { useDspConnectionTypesQuery } from "~/modules/Dictionary/queries";

const formatDspLabel = (v: string) =>
  v ? v.replace(/^p2p_/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : v;

const getDspDisplayLabel = (name: string, label?: string, showRawName?: boolean) => {
  const pretty = label || formatDspLabel(name);
  return showRawName && name ? `${pretty} (${name})` : pretty;
};

export type ISelectDspProps = Omit<ISelectProps<string | null>, "items"> & {
  showRawName?: boolean;
};

export const SelectDspConnectionType: React.FC<ISelectDspProps> = ({
  intent,
  showRawName = true,
  ...props
}) => {
  const { data: dspTypes = [], isPending } = useDspConnectionTypesQuery();

  const items = useMemo(() => {
    const byValue = new Map<string, { label: string; value: string; sortLabel: string }>();

    dspTypes.forEach((t) => {
      if (t?.name) {
        byValue.set(t.name, {
          label: getDspDisplayLabel(t.name, t.label, showRawName),
          value: t.name,
          sortLabel: t.label || formatDspLabel(t.name),
        });
      }
    });

    if (typeof props.value === "string" && props.value) {
      if (!byValue.has(props.value)) {
        byValue.set(props.value, {
          label: getDspDisplayLabel(props.value, undefined, showRawName),
          value: props.value,
          sortLabel: formatDspLabel(props.value),
        });
      }
    }

    return sortByString([...byValue.values()], "sortLabel", TableSortDirection.ASC);
  }, [dspTypes, props.value, showRawName]);

  return (
    <SelectInput
      {...props}
      intent={intent}
      items={items}
      value={props.value ?? null}
      clearable
      searchable
      placeholder={props.placeholder ?? 'Select DSP'}
      rightIcon={isPending && !props.disabled ? <i className="fa fa-spinner fa-spin" /> : props.rightIcon}
    />
  );
};

export const SelectDspConnectionTypeField = (props: IFieldProps<ISelectDspProps>) => (
  <FormField {...props}>
    <SelectDspConnectionType
      {...props.options}
      intent={hasError(props) && Intent.DANGER}
      value={props.field.value}
      onChange={val => onChangeField(props, val)}
    />
  </FormField>
);

export type IMultiSelectDspProps = Omit<IMultiSelectProps<string>, "items"> & {
  showRawName?: boolean;
};

export const MultiSelectDspConnectionType: React.FC<IMultiSelectDspProps> = ({
  intent,
  showRawName = true,
  ...props
}) => {
  const { data: dspTypes = [], isPending } = useDspConnectionTypesQuery();

  const items = useMemo(() => {
    const byValue = new Map<string, { label: string; value: string; sortLabel: string }>();

    dspTypes.forEach((t) => {
      if (t?.name) {
        byValue.set(t.name, {
          label: getDspDisplayLabel(t.name, t.label, showRawName),
          value: t.name,
          sortLabel: t.label || formatDspLabel(t.name),
        });
      }
    });

    (props.value ?? []).forEach((v) => {
      if (v && !byValue.has(v)) {
        byValue.set(v, {
          label: getDspDisplayLabel(v, undefined, showRawName),
          value: v,
          sortLabel: formatDspLabel(v),
        });
      }
    });

    return sortByString([...byValue.values()], "sortLabel", TableSortDirection.ASC);
  }, [dspTypes, props.value, showRawName]);

  return (
    <MultiSelectInput
      {...props}
      intent={intent}
      items={items}
      value={props.value ?? []}
      searchable
      clearable
      placeholder={props.placeholder ?? 'Filter by DSP'}
      rightIcon={isPending && !props.disabled ? <i className="fa fa-spinner fa-spin" /> : props.rightIcon}
    />
  );
};

export const MultiSelectDspConnectionTypeField = (props: IFieldProps<IMultiSelectDspProps>) => (
  <FormField {...props}>
    <MultiSelectDspConnectionType
      {...props.options}
      intent={hasError(props) && Intent.DANGER}
      value={props.field.value}
      onChange={vals => onChangeField(props, vals)}
    />
  </FormField>
);
